import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Select,
} from 'antd'
import { inject, observer } from 'mobx-react'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import EmptyContent from '../../../components/EmptyContent'
import { DATE_FORMAT_LIST, ISO_DATE_FORMAT } from '../../../constants'
import utils from '../../../utils'

const { RangePicker } = DatePicker
const { Option } = Select

const AdvanceFilterBlock = props => {
  const {
    bookStore,
    loadingAnimationStore,
    authorityStore,
    handleChangeParams,
    internalDocumentStore,
    onClose,
  } = props

  const { queryParamsOutgoing } = internalDocumentStore
  const { bookGroupList, bookListOfGroup } = bookStore
  const { authorityIssuedsList } = authorityStore

  const [form] = Form.useForm()
  const [loadingBook, setLoadingBook] = useState(false)

  // Sync state from query parameters initially and whenever it changes
  useEffect(() => {
    form.setFieldsValue({
      title: queryParamsOutgoing.title || undefined,
      document_number: queryParamsOutgoing.document_number || undefined,
      book_group_id: queryParamsOutgoing.book_group_id || undefined,
      book_id: queryParamsOutgoing.book_id || undefined,
      date_issued:
        queryParamsOutgoing.from_date_issued && queryParamsOutgoing.to_date_issued
          ? [
              moment(queryParamsOutgoing.from_date_issued),
              moment(queryParamsOutgoing.to_date_issued),
            ]
          : undefined,
      document_type: queryParamsOutgoing.document_type || undefined,
      urgency_level: queryParamsOutgoing.urgency_level || undefined,
      authority_name: queryParamsOutgoing.authority_name
        ? [queryParamsOutgoing.authority_name]
        : [],
      signer: queryParamsOutgoing.signer || undefined,
      is_sign: queryParamsOutgoing.is_sign !== null ? queryParamsOutgoing.is_sign : undefined,
      only_unread: queryParamsOutgoing.only_unread ? true : false,
    })

    if (queryParamsOutgoing.book_group_id) {
      const fetchBooks = async () => {
        try {
          await bookStore.getBookByBookGroupId(queryParamsOutgoing.book_group_id)
        } catch (error) {
          console.log(error)
        }
      }
      fetchBooks()
    }
  }, [queryParamsOutgoing, form, bookStore])

  const onChangeBookGroupId = async book_group_id => {
    form.setFieldsValue({ book_id: undefined })
    if (!book_group_id) {
      bookStore.clearBookListOfGroup()
      return
    }
    setLoadingBook(true)
    try {
      await bookStore.getBookByBookGroupId(book_group_id)
    } catch (err) {
      console.log(err)
    } finally {
      setLoadingBook(false)
    }
  }

  const onFinish = values => {
    handleChangeParams({
      title: queryStringParse.title || null,
      document_number: values.document_number?.trim() || null,
      book_group_id: values.book_group_id || null,
      book_id: values.book_id || null,
      from_date_issued: values.date_issued
        ? moment(values.date_issued[0]).format(ISO_DATE_FORMAT)
        : null,
      to_date_issued: values.date_issued
        ? moment(values.date_issued[1]).format(ISO_DATE_FORMAT)
        : null,
      document_type: values.document_type || null,
      urgency_level: values.urgency_level || null,
      authority_name:
        values.authority_name?.length > 0
          ? values.authority_name[values.authority_name.length - 1]
          : null,
      signer: values.signer?.trim() || null,
      is_sign: values.is_sign !== undefined ? values.is_sign : null,
      only_unread: values.only_unread ? true : false,
    })
    if (onClose) onClose()
  }

  const filterSelectOption = (input, option) => {
    return (
      utils
        .nonAccentVietnamese(option.name)
        .indexOf(utils.nonAccentVietnamese(input)) >= 0
    )
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{ width: '100%', maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
      className="advance-filter-form"
    >
      <style>{`
        .advance-filter-form .ant-form-item {
          margin-bottom: 16px;
        }
        .advance-filter-form .ant-form-item-label > label {
          color: #333;
        }
        .advance-filter-form .ant-input,
        .advance-filter-form .ant-input-affix-wrapper,
        .advance-filter-form .ant-picker,
        .advance-filter-form .ant-select-selector {
          border-radius: 8px !important;
        }
      `}</style>

      <Form.Item label="Số hiệu" name="document_number">
        <Input placeholder="Số hiệu" allowClear />
      </Form.Item>

      <Form.Item label="Nhóm sổ văn bản" name="book_group_id">
        <Select
          loading={loadingAnimationStore.tableLoading}
          allowClear
          onChange={onChangeBookGroupId}
          showSearch
          notFoundContent={<EmptyContent />}
          optionFilterProp={'name'}
          placeholder={'Chọn nhóm sổ văn bản'}
        >
          {bookGroupList.map(book => (
            <Option key={book.id} value={book.id} name={book.name}>
              {book.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        noStyle
        dependencies={['book_group_id']}
      >
        {({ getFieldValue }) => {
          const bookGroupId = getFieldValue('book_group_id')
          return (
            <Form.Item label="Sổ văn bản" name="book_id">
              <Select
                loading={loadingBook}
                allowClear
                showSearch
                notFoundContent={<EmptyContent />}
                filterOption={true}
                optionFilterProp={'name'}
                disabled={!bookGroupId}
                placeholder={'Chọn sổ văn bản'}
              >
                {bookListOfGroup.map(book => (
                  <Option key={book.id} value={book.id} name={book.name}>
                    {book.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )
        }}
      </Form.Item>

      <Form.Item label="Ngày ban hành" name="date_issued">
        <RangePicker
          placeholder={['Từ ngày', 'Đến ngày']}
          format={DATE_FORMAT_LIST}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item label="Loại văn bản" name="document_type">
        <Select
          allowClear
          notFoundContent={<EmptyContent />}
          placeholder={'Chọn loại văn bản'}
        >
          <Option value={'020'}>Báo cáo</Option>
          <Option value={'021'}>Công văn</Option>
          <Option value={'022'}>Quyết định</Option>
          <Option value={'023'}>Thông báo - Giấy mời</Option>
          <Option value={'024'}>Tờ trình</Option>
          <Option value={'025'}>Khác</Option>
          <Option value={'026'}>Phiếu lấy ý kiến</Option>
          <Option value={'027'}>Kế hoạch</Option>
          <Option value={'028'}>Biên bản</Option>
          <Option value={'029'}>Quy định</Option>
          <Option value={'030'}>Giấy mời</Option>
          <Option value={'031'}>Công điện</Option>
          <Option value={'032'}>Chương trình</Option>
          <Option value={'033'}>Luật</Option>
          <Option value={'034'}>Điều lệ</Option>
          <Option value={'035'}>Nghị quyết</Option>
          <Option value={'036'}>Chỉ thị</Option>
          <Option value={'037'}>Thông tư</Option>
          <Option value={'038'}>Giấy đi đường</Option>
          <Option value={'039'}>Công văn Đảng ủy</Option>
          <Option value={'040'}>Công văn Công đoàn</Option>
          <Option value={'041'}>Bộ Công Thương</Option>
          <Option value={'042'}>Kiểm định</Option>
          <Option value={'043'}>Đào tạo</Option>
          <Option value={'044'}>Hợp đồng</Option>
        </Select>
      </Form.Item>

      <Form.Item label="Mức độ xử lý" name="urgency_level">
        <Select
          allowClear
          notFoundContent={<EmptyContent />}
          placeholder={'Chọn độ khẩn'}
        >
          <Option value={'001'}>Thường</Option>
          <Option value={'002'}>Quan trọng</Option>
          <Option value={'003'}>Rất quan trọng</Option>
        </Select>
      </Form.Item>

      <Form.Item label="Bộ phận dự thảo" name="authority_name">
        <Select
          mode="tags"
          filterOption={filterSelectOption}
          allowClear
          showSearch
          showArrow
          optionFilterProp={'name'}
          notFoundContent={<EmptyContent />}
          placeholder={'Chọn hoặc nhập bộ phận dự thảo'}
          onChange={val => {
            form.setFieldsValue({
              authority_name: val.length > 0 ? [val[val.length - 1]] : [],
            })
          }}
        >
          {authorityIssuedsList.map(authorityIssued => (
            <Option
              name={authorityIssued.name}
              value={authorityIssued.name}
              key={authorityIssued.id}
            >
              {authorityIssued.name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="Người ký" name="signer">
        <Input placeholder={'Người ký'} allowClear />
      </Form.Item>

      <Form.Item label="Văn bản ký số" name="is_sign">
        <Select allowClear placeholder="-- Chọn trạng thái --">
          <Option value={true}>Đã ký số</Option>
          <Option value={false}>Không ký số</Option>
        </Select>
      </Form.Item>

      <Form.Item label="Trạng thái văn bản" name="only_unread" valuePropName="checked">
        <Checkbox>Hiển thị văn bản chưa đọc</Checkbox>
      </Form.Item>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 24, marginBottom: 8 }}>
        <Button onClick={onClose} style={{ borderRadius: 8, flex: 1, height: 40 }}>
          Huỷ
        </Button>
        <Button type="primary" htmlType="submit" style={{ borderRadius: 8, flex: 1, height: 40 }}>
          Áp dụng
        </Button>
      </div>
    </Form>
  )
}

export default inject(
  'bookStore',
  'loadingAnimationStore',
  'authorityStore',
  'internalDocumentStore'
)(observer(AdvanceFilterBlock))
